import React from "react";
import {
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { KeyboardArrowDown as ArrowDownIcon } from "@mui/icons-material";
import { ChatInput } from "../../components/chat/ChatInput";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface ReviewStepProps {
  reviewContent: string;
  generatedExamples: string[];
  messages: ChatMessage[];
  isLoading: boolean;
  chatContainerRef: React.RefObject<HTMLDivElement | null>;
  onChatScroll: () => void;
  showScrollArrow: boolean;
  onScrollToBottom: () => void;
  onHideScrollArrow: () => void;
  onSendMessage: (message: string) => void;
  onBack: () => void;
  onSignOff: () => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  reviewContent,
  generatedExamples,
  messages,
  isLoading,
  chatContainerRef,
  onChatScroll,
  showScrollArrow,
  onScrollToBottom,
  onHideScrollArrow,
  onSendMessage,
  onBack,
  onSignOff,
}) => {
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "primary.main", mb: 0.5 }}
          >
            Review Template
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review the example report format below. Sign off when satisfied, or
            use the chat to request changes.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: 2 }}
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="success"
            sx={{ textTransform: "none", borderRadius: 2 }}
            onClick={onSignOff}
          >
            Sign Off
          </Button>
        </Box>
      </Box>

      {/* Split layout: document left, chat right */}
      <Box
        sx={{
          display: "flex",
          height: 520,
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {/* Left: read-only document */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              bgcolor: alpha(theme.palette.action.hover, 0.04),
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Example Report
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 2,
              fontFamily: "monospace",
              fontSize: "0.85rem",
              whiteSpace: "pre-wrap",
              color: "text.primary",
              bgcolor: "background.default",
            }}
          >
            {generatedExamples.length > 0
              ? generatedExamples.join("\n\n---\n\n")
              : reviewContent}
          </Box>
          {isLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.background.default, 0.6),
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
        </Box>

        {/* Right: chat panel */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              bgcolor: alpha(theme.palette.action.hover, 0.04),
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              AI Assistant
            </Typography>
          </Box>
          <Box
            ref={chatContainerRef}
            onScroll={onChatScroll}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              position: "relative",
            }}
          >
            {messages.length === 0 && !isLoading && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", mt: 4 }}
              >
                Ask the AI to modify the template. For example: &quot;Add a
                witness section&quot; or &quot;Make the incident description more
                detailed.&quot;
              </Typography>
            )}
            {messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  justifyContent:
                    msg.role === "user" ? "flex-end" : "flex-start",
                }}
              >
                <Box
                  sx={{
                    maxWidth: "85%",
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor:
                      msg.role === "user"
                        ? "primary.main"
                        : alpha(theme.palette.action.hover, 0.08),
                    color:
                      msg.role === "user"
                        ? "primary.contrastText"
                        : "text.primary",
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {msg.content}
                  </Typography>
                </Box>
              </Box>
            ))}
            {isLoading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  "@keyframes fadeInUp": {
                    from: {
                      opacity: 0,
                      transform: "translateY(8px)",
                    },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                  animation: "fadeInUp 0.3s ease-out",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.action.hover, 0.08),
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      alignItems: "center",
                      "@keyframes bounce": {
                        "0%, 60%, 100%": {
                          transform: "translateY(0)",
                        },
                        "30%": { transform: "translateY(-4px)" },
                      },
                      "& span": {
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: "text.secondary",
                        display: "inline-block",
                        animation: "bounce 1.4s infinite ease-in-out",
                      },
                      "& span:nth-of-type(1)": {
                        animationDelay: "0s",
                      },
                      "& span:nth-of-type(2)": {
                        animationDelay: "0.2s",
                      },
                      "& span:nth-of-type(3)": {
                        animationDelay: "0.4s",
                      },
                    }}
                  >
                    <span />
                    <span />
                    <span />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Editing template
                  </Typography>
                </Box>
              </Box>
            )}
            {showScrollArrow && (
              <IconButton
                onClick={() => {
                  onScrollToBottom();
                  onHideScrollArrow();
                }}
                sx={{
                  position: "sticky",
                  bottom: 0,
                  alignSelf: "center",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  width: 36,
                  height: 36,
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                  boxShadow: 2,
                }}
              >
                <ArrowDownIcon />
              </IconButton>
            )}
          </Box>
          <ChatInput
            onSend={onSendMessage}
            disabled={isLoading}
            isStreaming={isLoading}
          />
        </Box>
      </Box>
    </>
  );
};

export default ReviewStep;
